import React, { useState, useRef, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useIntl, FormattedMessage } from "react-intl";
import { bindActionCreators } from "redux";
import { Button, Divider, Table, TableBody, TableCell, TableHead, TableRow, TableFooter } from "@material-ui/core";
import { Caption } from "../workOrderDetails";
import AttachmentRow from "./attachmentRow";
import { Lightbox } from "../../common/attachments/lightbox";
import { showAlert, ALERT_TYPES } from "../../../redux/alert/alertActions";
import {
    formatFilesForUpload,
    formatFileOnInput,
    convertByteArray,
    resizeFile,
    isValidImageExt,
    getSupportedFileExtensions,
} from "../../common/attachments/attachmentsHelper.js";
import {
    fetchAttachments,
    uploadAttachments,
    setFilesToUpload,
    removeAttachment,
    updateAttachments,
    fileUploadAuditActivity,
    fileUpdateAuditActivity,
} from "../../../redux/attachments/attachmentsActions";
import AttachmentRowEdit from "./attachmentRowEdit";
import { areAttachmentsEnabled } from "../../../globals/workOrderStatus";
import { fileMessages } from "./messages";
import { useStyles } from "./attachmentsInfoStyles";
import { launchSketchTool } from "./launchSketchTool";
import useIsEndToEndEnabled from "../../../hooks/useIsEndToEndEnabled";

const AttachmentsInfo = (props) => {
    const intl = useIntl();
    const classes = useStyles();
    const { workOrderNumber, clickDispatchId } = props;
    const [disableUpload, setDisableUpload] = useState(false);
    const [editing, setEditing] = useState(false);
    const [files, setFiles] = useState([]);
    const [editedFiles, setEditedFiles] = useState([]);
    const [fileIndex, setFileIndex] = useState(null);
    const [open, setOpen] = useState(false);
    const [uploadFlag, setUploadFlag] = useState(false);
    const [updateFlag, setUpdateFlag] = useState(false);
    const [filesBeforeUpdate, setFilesBeforeUpdate] = useState([]);
    const inputFile = useRef(null);
    const isEndToEndEnabled = useIsEndToEndEnabled(workOrderNumber);

    useEffect(() => {
        if (workOrderNumber) props.fetchAttachments(workOrderNumber);
    }, [workOrderNumber]);

    useEffect(() => {
        setFiles([...props.workOrderAttachments]);
        if (
            uploadFlag &&
            props.fileUploadResponse.length > 0 &&
            typeof props.fileUploadResponse[0] === "object" &&
            props.workOrderAttachments.length > 0
        ) {
            props.fileUploadResponse.map((file, index) => {
                const attachmentID = file.fileDownloadUri.split("/").at(-1);
                const uploadedAttachment = props.workOrderAttachments.filter((attachment) => {
                    return attachment.id === attachmentID;
                })[0];
                const payload = {
                    createdDateTime: uploadedAttachment.createdDateTime,
                    id: uploadedAttachment.id,
                    newFileDescription: "",
                    newFileName: "",
                    newFileType: "",
                    newRelatedJobId: "",
                    newRelatedSourceId: "",
                    oldFileDescription: uploadedAttachment.fileDescription,
                    oldFileName: uploadedAttachment.fileName,
                    oldFileType: uploadedAttachment.fileType,
                    oldRelatedJobId: uploadedAttachment.relatedJobId,
                    oldRelatedSourceId: uploadedAttachment.relatedSourceId,
                    originatingSystem: "CMIS",
                    uploadedBy: props.userProfile.username,
                };
                const closedFun = (payload1, index1) => {
                    setTimeout(() => {
                        props.fileUploadAuditActivity(payload1);
                    }, index1 * 2000);
                };
                closedFun(payload, index);
            });
            setUploadFlag(false);
        }
        if (updateFlag && props.fileUpdateResponse.length > 0 && typeof props.fileUpdateResponse[0] === "object" && filesBeforeUpdate.length > 0) {
            props.fileUpdateResponse.map((file, index) => {
                let updatedFileOld = filesBeforeUpdate.filter((file1) => {
                    return file1.id === file.id;
                })[0];
                const payload = {
                    createdDateTime: updatedFileOld.createdDateTime,
                    id: updatedFileOld.id,
                    newFileDescription: file.fileDescription,
                    newFileName: file.fileName,
                    newFileType: "",
                    newRelatedJobId: "",
                    newRelatedSourceId: "",
                    oldFileDescription: updatedFileOld.fileDescription,
                    oldFileName: updatedFileOld.fileName,
                    oldFileType: updatedFileOld.fileType,
                    oldRelatedJobId: updatedFileOld.relatedJobId,
                    oldRelatedSourceId: updatedFileOld.relatedSourceId,
                    originatingSystem: "CMIS",
                    uploadedBy: props.userProfile.username,
                };
                const closedFun = (payload1, index1) => {
                    setTimeout(() => {
                        props.fileUpdateAuditActivity(payload1);
                    }, index1 * 2000);
                };
                closedFun(payload, index);
            });
            setFilesBeforeUpdate([]);
            setUpdateFlag(false);
        }
    }, [props.workOrderAttachments]);

    const onFileEdit = (file) => {
        let fileIndex = editedFiles.findIndex((f) => f.id === file.id);
        if (fileIndex >= 0) {
            let newFiles = [...editedFiles];
            newFiles[fileIndex] = file;
            setEditedFiles(newFiles);
        } else setEditedFiles([...editedFiles, file]);
    };

    const onSave = () => {
        const filesToBeUpdated = formatFilesForUpload(editedFiles, clickDispatchId, workOrderNumber, true);
        // send the update and refresh the work order
        props.updateAttachments(filesToBeUpdated);
        setUpdateFlag(true);

        // toggle edit mode
        setEditing(false);
    };

    const onCancel = () => {
        setEditing(false);
        setFiles([...props.workOrderAttachments]);
    };

    const openFileSelect = () => {
        inputFile.current.click();
    };

    const handleAttachmentSelect = (index) => {
        setFileIndex(index);
        setOpen(true);
    };

    const handleFileInput = async (e) => {
        const { files } = e.target;
        if (files && files.length > 0) {
            var formattedFiles = [];
            for (let i = 0; i < files.length; i++) {
                var file = files[i];
                if (file.type.includes("video")) {
                    props.showAlert(Date.now(), fileMessages.videoError, ALERT_TYPES.error);
                    continue; // Skip videos
                }
                if (!file.name.includes(".") || !isValidImageExt(`.${file.name.split(".")[file.name.split(".").length - 1]}`)) {
                    props.showAlert(Date.now(), fileMessages.NoAcceptableFileError, ALERT_TYPES.error);
                    continue;
                }
                if (file.type.includes("image") && file.size > 2000000) {
                    // If image size is above 2mb, compress
                    file = await resizeFile(file);
                }
                const data = await convertByteArray(file);
                var formattedFile = formatFileOnInput(file, data, clickDispatchId, workOrderNumber, props.userProfile.username);
                formattedFiles.push(formattedFile);
            }
            props.setFilesToUpload(formattedFiles);
        }
    };

    const removeNewRow = (id) => {
        const newSelectedFiles = props.filesToUpload.filter((f) => f.id !== id);
        props.setFilesToUpload(newSelectedFiles);
        setDisableUpload(newSelectedFiles.some((f) => !f.fileName || f.fileName.trim() === ""));
    };

    const handleFilesUpload = () => {
        const filesToBeUploaded = formatFilesForUpload(props.filesToUpload, clickDispatchId, workOrderNumber);
        props.uploadAttachments(filesToBeUploaded);
        props.setFilesToUpload([]);
        setUploadFlag(true);
    };

    const handleRemoveAttachment = (file) => {
        const fileDetails = {
            id: file.id,
            workOrderNumber: workOrderNumber,
            payload: {
                createdDateTime: file.createdDateTime,
                id: file.id,
                newFileDescription: "",
                newFileName: "",
                newFileType: "",
                newRelatedJobId: "",
                newRelatedSourceId: "",
                oldFileDescription: file.fileDescription,
                oldFileName: file.fileName,
                oldFileType: file.fileType,
                oldRelatedJobId: file.relatedJobId,
                oldRelatedSourceId: file.relatedSourceId,
                originatingSystem: "CMIS",
                uploadedBy: props.userProfile.username,
            },
        };
        props.removeAttachment(fileDetails);
    };
    const handleSetEdit = () => {
        setFilesBeforeUpdate(props.workOrderAttachments);
        setEditing(true);
    };

    const isAddFilesDisabled = useMemo(() => {
        return !props.workOrderStatus || !areAttachmentsEnabled(props.workOrderStatus) || editing || props.filesToUpload.length > 0;
    }, [props.workOrderStatus, areAttachmentsEnabled, editing, props.filesToUpload.length]);

    return (
        <>
            <Lightbox open={open} onClose={() => setOpen(false)} fileIndex={fileIndex} files={files} />
            <div className={classes.subheader}>
                <span className={classes.subheaderText}>
                    <FormattedMessage
                        id="admin.work-order-details.attachments-table.subheader"
                        defaultMessage="Attached files"
                        description="Attachments table subheader"
                    />
                </span>
                {props.workOrderStatus && areAttachmentsEnabled(props.workOrderStatus) && (
                    <Button color="primary" onClick={handleSetEdit}>
                        <FormattedMessage
                            id="admin.work-order-details.attachments-table.edit-button"
                            defaultMessage="Edit"
                            description="Attachments table edit button"
                        />
                    </Button>
                )}
            </div>
            <Table aria-label="simple table" className={classes.table}>
                <TableHead className={classes.header}>
                    <TableRow>
                        <TableCell align="left" className={classes.fileColumn}>
                            <Caption>{intl.formatMessage(fileMessages.file)}</Caption>
                        </TableCell>
                        <TableCell align="left" className={classes.uploadedColumn}>
                            <Caption>Type</Caption>
                        </TableCell>
                        <TableCell align="left" className={classes.uploadedColumn}>
                            <Caption>{intl.formatMessage(fileMessages.uploadedBy)}</Caption>
                        </TableCell>
                        <TableCell align="left" className={classes.uploadedColumn}>
                            <Caption>{intl.formatMessage(fileMessages.uploadedOn)}</Caption>
                        </TableCell>
                        <TableCell align="left">
                            <Caption>{intl.formatMessage(fileMessages.description)}</Caption>
                        </TableCell>
                        <TableCell align="center" className={classes.deleteColumn}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {files.map((file, i) =>
                        editing ? (
                            <AttachmentRowEdit
                                key={file.id}
                                file={file}
                                editing
                                handleAttachmentSelect={() => handleAttachmentSelect(i)}
                                workOrderStatus={props.workOrderStatus}
                                onChange={(file) => onFileEdit(file)}
                                onRemove={() => handleRemoveAttachment(file)}
                            />
                        ) : (
                            <AttachmentRow
                                key={file.id}
                                file={file}
                                handleAttachmentSelect={() => handleAttachmentSelect(i)}
                                workOrderStatus={props.workOrderStatus}
                                onRemove={() => handleRemoveAttachment(file)}
                            />
                        ),
                    )}
                    {props.filesToUpload.length > 0 &&
                        props.filesToUpload.map((file, i) => (
                            <AttachmentRowEdit
                                key={file.id}
                                file={file}
                                onChange={(file) => {
                                    setDisableUpload(!file.fileName || file.fileName.trim() === "");
                                    const fileIndex = props.filesToUpload.findIndex((f) => f.id === file.id);
                                    if (fileIndex >= 0) {
                                        let copy = [...props.filesToUpload];
                                        copy[fileIndex] = file;
                                        props.setFilesToUpload(copy);
                                    }
                                }}
                                removeNewRow={removeNewRow}
                            />
                        ))}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell align="left">
                            <input
                                style={{ display: "none" }}
                                multiple
                                ref={inputFile}
                                onClick={(event) => (event.target.value = null)}
                                onChange={handleFileInput}
                                type="file"
                                accept={getSupportedFileExtensions().toString()}
                            />
                            <Button variant="outlined" color="primary" onClick={openFileSelect} disabled={isAddFilesDisabled}>
                                {intl.formatMessage(fileMessages.addFiles)}
                            </Button>
                            {isEndToEndEnabled && (
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() => launchSketchTool(workOrderNumber)}
                                    disabled={isAddFilesDisabled}>
                                    {intl.formatMessage(fileMessages.launchSketchTools)}
                                </Button>
                            )}
                            {props.filesToUpload.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleFilesUpload}
                                    style={{ marginLeft: "5px" }}
                                    disabled={disableUpload}>
                                    {intl.formatMessage(fileMessages.uploadFiles)}
                                </Button>
                            )}
                        </TableCell>
                    </TableRow>
                    {editing && (
                        <>
                            <TableRow>
                                <TableCell align="right" colSpan={"5"}>
                                    <Divider />
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="right" colSpan={"4"} onClick={() => onCancel()}>
                                    <Button tertiary>
                                        <FormattedMessage
                                            id="admin.work-order-details.sku-list.cancel-sku-edit-button-caption"
                                            defaultMessage="Cancel"
                                            description="Admin work order sku list cancel SKU edit button caption"
                                        />
                                    </Button>
                                </TableCell>
                                <TableCell align="right" colSpan={"1"} onClick={() => onSave()}>
                                    <Button
                                        secondary
                                        disabled={
                                            editedFiles.length === 0 || editedFiles.some((f) => !f.fileNameNoExt || f.fileNameNoExt.trim() === "")
                                        }>
                                        <FormattedMessage
                                            id="admin.work-order-details.sku-list.save-sku-edit-button-caption"
                                            defaultMessage="Save changes"
                                            description="Admin work order sku list save SKU edit button caption"
                                        />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </>
                    )}
                </TableFooter>
            </Table>
        </>
    );
};

const mapStateToProps = (state, prevProps) => {
    return {
        workOrderAttachments: state.attachments?.workOrderAttachments,
        workOrderStatus: state.tasks?.workorderData?.workorderData?.status,
        filesToUpload: state.attachments?.filesToUpload,
        fileUploadResponse: state.attachments?.fileUploadRes,
        fileUpdateResponse: state.attachments?.fileUpdateRes,
        userProfile: state.login.userProfile,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchAttachments,
            uploadAttachments,
            updateAttachments,
            setFilesToUpload,
            removeAttachment,
            showAlert,
            fileUploadAuditActivity,
            fileUpdateAuditActivity,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentsInfo);
